import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  onboardingApiClient,
  wxApiClient,
} from "@shared/api/clients/apiClientFactory";
import {
  CreateMasterSopRequest,
  ServiceAssociationResponse,
  ServiceProcedureSummaryResponse,
  SopConfigResponse,
  SopDetailsParams,
  SopDetailsResponse,
  sopFilters,
  SopMaterialsResponse,
  SopReadingsResponse,
  SopTabDetailsParams,
  SopTabDetailsResponse,
  SopTasksResponse,
  SOPTemplateResponse,
  UpdateSopDocumentsRequest,
  UpdateSopDocumentsResponse,
  UpdateSopMaterialsRequest,
  UpdateSopReadingsRequest,
  UpdateSopResponse,
  UpdateSopTasksRequest,
  UtilityServicesResponse,
} from "./types";

export const serviceProcedureApi = {
  // Get service procedure summary
  getServiceProcedureSummary: async (
    utilityId: string
  ): Promise<ServiceProcedureSummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "service-procedure-summary",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },
  getSopDetails: async (
    params: Pick<SopDetailsParams, "id" | "remoteUtilityId">
  ): Promise<SopDetailsResponse> => {
    const { id, remoteUtilityId } = params;
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `service-procedure-field/${id}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        // Removed fieldName parameter - now gets all data
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },
  getSopTabDetails: async (
    params: Pick<SopTabDetailsParams, "id" | "remoteUtilityId" | "fieldName">
  ): Promise<SopTabDetailsResponse> => {
    const { id, remoteUtilityId, fieldName } = params;

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `service-procedure-field/${id}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        query.push("field_name", fieldName);
        query.push("is_active", true);
      }
    );

    const response = await wxApiClient.get(url);
    return response.data;
  },
  getServiceAssociations: async (
    utilityId: string
  ): Promise<ServiceAssociationResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "choices/service_association",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Add new task to SOP
  addSopTask: async (params: {
    sopId: number;
    taskData: {
      name: string;
      time: string;
      skill_id: number[];
      is_photo_req: string;
      downtime?: string;
    };
  }): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", "sop-quick-service");
    const response = await wxApiClient.post(url, {
      sop_id: params.sopId,
      task_data: params.taskData,
    });
    return response.data;
  },

  // Edit existing task in SOP
  editSopTask: async (params: {
    sopId: number;
    existingValue: string;
    taskData: {
      name: string;
      time: string;
      skill_id: number[];
      is_photo_req: string;
      downtime?: string;
    };
  }): Promise<any> => {
    const url = ApiEndpoints.createUrl(
      "wx",
      `sop-quick-service/${params.sopId}`
    );
    const response = await wxApiClient.patch(url, {
      existing_value: params.existingValue,
      task_data: params.taskData,
    });
    return response.data;
  },

  // Add new material to SOP
  addSopMaterial: async (params: {
    sopId: number;
    materialData: {
      name: string;
      size?: string;
      quantity_required: string;
      cost: string;
    };
  }): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", "sop-quick-service");
    const response = await wxApiClient.post(url, {
      sop_id: params.sopId,
      material_data: params.materialData,
    });
    return response.data;
  },
  cloneSopTemplate: async (templateId: number): Promise<any> => {
    const url = ApiEndpoints.createUrl(
      "wx",
      `service-procedure-clone/${templateId}`
    );
    const response = await wxApiClient.put(url);
    return response.data;
  },

  // Edit existing material in SOP
  editSopMaterial: async (params: {
    sopId: number;
    existingValue: string;
    materialData: {
      name: string;
      size?: string;
      quantity: string;
      cost: string;
    };
  }): Promise<any> => {
    const url = ApiEndpoints.createUrl(
      "wx",
      `sop-quick-service/${params.sopId}`
    );
    const response = await wxApiClient.patch(url, {
      existing_value: params.existingValue,
      material_data: params.materialData,
    });
    return response.data;
  },

  // Add new reading to SOP
  addSopReading: async (params: {
    sopId: number;
    readingData: {
      name: string;
      unit: string;
      minimum?: string;
      maximum?: string;
      is_photo_req: string;
    };
  }): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", "sop-quick-service");
    const response = await wxApiClient.post(url, {
      sop_id: params.sopId,
      readings_data: params.readingData, // Changed from reading_data to readings_data
    });
    return response.data;
  },

  // Edit existing reading in SOP
  editSopReading: async (params: {
    sopId: number;
    existingValue: string;
    readingData: {
      name: string;
      unit: string;
      minimum?: string;
      maximum?: string;
      is_photo_req: string;
    };
  }): Promise<any> => {
    const url = ApiEndpoints.createUrl(
      "wx",
      `sop-quick-service/${params.sopId}`
    );
    const response = await wxApiClient.patch(url, {
      existing_value: params.existingValue,
      readings_data: params.readingData, // Changed from reading_data to readings_data
    });
    return response.data;
  },

  // Get SOP notes
  getSopNotes: async (sopId: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "service-procedure/notes",
      (query) => {
        query.push("sop_id", sopId.toString());
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Add new note to SOP
  addSopNote: async (params: {
    sopId: number;
    remoteUtilityId: number;
    noteData: {
      code: string;
      name: string;
      is_active: boolean;
      note_color: string;
      note_description: string;
    };
  }): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", "service-procedure/notes");
    const response = await wxApiClient.post(url, {
      sop_id: params.sopId,
      remote_utility_id: params.remoteUtilityId,
      notes: params.noteData,
    });
    return response.data;
  },

  // Edit existing note in SOP
  editSopNote: async (params: {
    sopId: number;
    noteData: {
      code: string;
      name: string;
      is_active: boolean;
      note_color: string;
      note_description: string;
    };
  }): Promise<any> => {
    const url = ApiEndpoints.createUrl(
      "wx",
      `service-procedure/notes/${params.sopId}`
    );
    const response = await wxApiClient.put(url, params.noteData);
    return response.data;
  },
  deleteSopNote: async (params: {
    sopId: number;
    noteCode: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `service-procedure/notes/${params.sopId}`,
      (query) => {
        query.push("code", params.noteCode);
      }
    );
    const response = await wxApiClient.delete(url);
    return response.data;
  },
  createSop: async (data: CreateMasterSopRequest): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", "service-procedure");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },
  updateSopById: async (
    sopId: number,
    data: CreateMasterSopRequest
  ): Promise<UpdateSopResponse> => {
    const url = ApiEndpoints.createUrl("wx", `service-procedure/${sopId}`);
    const response = await wxApiClient.patch(url, data);
    return response.data;
  },
  updateSopTasks: async (
    sopId: number,
    data: UpdateSopTasksRequest
  ): Promise<UpdateSopResponse> => {
    const url = ApiEndpoints.createUrl("wx", `service-procedure/${sopId}`);
    const response = await wxApiClient.patch(url, data);
    return response.data;
  },

  // Update SOP with materials
  updateSopMaterials: async (
    sopId: number,
    data: UpdateSopMaterialsRequest
  ): Promise<UpdateSopResponse> => {
    const url = ApiEndpoints.createUrl("wx", `service-procedure/${sopId}`);
    const response = await wxApiClient.patch(url, data);
    return response.data;
  },

  // Update SOP with readings
  updateSopReadings: async (
    sopId: number,
    data: UpdateSopReadingsRequest
  ): Promise<UpdateSopResponse> => {
    const url = ApiEndpoints.createUrl("wx", `service-procedure/${sopId}`);
    const response = await wxApiClient.patch(url, data);
    return response.data;
  },
  deleteSopTask: async (params: {
    sopId: number;
    taskName: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `sop-quick-service/${params.sopId}`,
      (query) => {
        query.push("field", "task");
        query.push("name", params.taskName);
      }
    );
    const response = await wxApiClient.delete(url);
    return response.data;
  },

  // Delete SOP material
  deleteSopMaterial: async (params: {
    sopId: number;
    materialName: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `sop-quick-service/${params.sopId}`,
      (query) => {
        query.push("field", "material");
        query.push("name", params.materialName);
      }
    );
    const response = await wxApiClient.delete(url);
    return response.data;
  },

  // Delete SOP reading
  deleteSopReading: async (params: {
    sopId: number;
    readingName: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `sop-quick-service/${params.sopId}`,
      (query) => {
        query.push("field", "readings");
        query.push("name", params.readingName);
      }
    );
    const response = await wxApiClient.delete(url);
    return response.data;
  },

  // Generic update SOP function (can be used for any type of update)
  updateSop: async (
    sopId: number,
    data:
      | UpdateSopTasksRequest
      | UpdateSopMaterialsRequest
      | UpdateSopReadingsRequest
  ): Promise<UpdateSopResponse> => {
    const url = ApiEndpoints.createUrl("wx", `service-procedure/${sopId}`);
    const response = await wxApiClient.patch(url, data);
    return response.data;
  },
  updateSopDocuments: async (
    sopId: number,
    data: UpdateSopDocumentsRequest
  ): Promise<UpdateSopDocumentsResponse> => {
    const url = ApiEndpoints.createUrl("wx", `service-procedure/${sopId}`);

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("remote_utility_id", data.remoteUtilityId);
    formData.append("documents", data.documents);

    if (data.file) {
      formData.append("file", data.file);
    }

    const response = await wxApiClient.patch(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },
  createMasterSOP: async (
    sopId: number,
    status: number,
    reviewPublishUpdated: boolean
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", `service-procedure/${sopId}`);
    const data = {
      status: status,
      review_publish_updated: reviewPublishUpdated,
    };
    const response = await wxApiClient.patch(url, data);
    return response.data;
  },
};

export const sopTemplatesApi = {
  // Updated getTemplates to accept filters instead of individual parameters
  getTemplates: async (
    utilityId: string,
    filters: sopFilters = {
      page: 0,
      limit: 0,
      remote_utility_id: "",
    }
  ): Promise<SOPTemplateResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "get-service-procedure",
      (query) => {
        // Always include utility ID and pagination
        query.push("remote_utility_id", utilityId);
        query.push("page", (filters.page || 1).toString());
        query.push("limit", (filters.limit || 50).toString());

        // Add is_draft filter
        if (filters.is_draft !== undefined) {
          query.push("is_draft", filters.is_draft ? "1" : "0");
        }

        // Add search if provided
        if (filters.search_data) {
          query.push("search_data", filters.search_data);
        }

        // Add status filter if provided and not 'all'
        if (filters.status && filters.status !== "all") {
          query.push("status", filters.status);
        }

        // Add utility type filter if provided and not 'all'
        if (filters.utilityType && filters.utilityType !== "all") {
          query.push("utility_type", filters.utilityType);
        }

        // Add priority filter if provided and not 'all'
        if (filters.priority && filters.priority !== "all") {
          query.push("priority", filters.priority);
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Keep the existing toggleTemplateStatus method unchanged
  toggleTemplateStatus: async (
    templateId: number,
    utilityId: string,
    isActive: boolean
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("wx", `service-procedure/${templateId}`);
    const response = await wxApiClient.patch(url, {
      id: templateId,
      remote_utility_id: utilityId,
      isActive: isActive,
    });
    return response.data;
  },
};

export const utilityApi = {
  // Get utility services
  getUtilityServices: async (
    utilityId: string
  ): Promise<UtilityServicesResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "utility/services",
      (query) => {
        query.push("utility_id", utilityId.toString());
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },
};

export const sopConfigApi = {
  // Get SOP types
  getSopTypes: async (utilityId: string): Promise<SopConfigResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/sop_json",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("config_level", "sop_type");
        query.push("show_inactive", "true");
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getTaxRates: async (utilityId: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/tax_rate",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  // Get SOP subtypes for a specific type
  getSopSubtypes: async (
    utilityId: string,
    typeCode: string
  ): Promise<SopConfigResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/sop_json",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("code_list", typeCode);
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
};

export const sopTasksApi = {
  // Get SOP tasks for specific operation type
  getSopTasks: async (
    utilityId: string,
    operationType: string
  ): Promise<SopTasksResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "sop/task",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("operation_type", operationType.toString());
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },
};

// Add this to your existing sopTasksApi or create sopMaterialsApi
export const sopMaterialsApi = {
  // Get SOP materials for specific operation type
  getSopMaterials: async (
    utilityId: string,
    operationType: string
  ): Promise<SopMaterialsResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "sop/material",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("operation_type", operationType.toString());
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },
};

export const sopReadingsApi = {
  // Get SOP readings for specific operation type
  getSopReadings: async (
    utilityId: string,
    operationType: string
  ): Promise<SopReadingsResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "sop/reading",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("operation_type", operationType.toString());
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },
};

export const getMasterActivityLogs = async (
  remote_utility_id: string,
  ff_id: string,
  page: number = 1,
  limit: number = 10
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "activityLog",
    "logs",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      query.push("field_force_id", ff_id);
      query.push("module", "wx");
      query.push("page", page.toString());
      query.push("limit", limit.toString());
    }
  );

  const response = await wxApiClient.get(url);
  return response.data; // Return full response with pagination info
};
